import { ApolloError } from "@apollo/client/core";
import type { GraphQLFormattedError } from "graphql";

import { GraphQLErrorCode } from "./errors.js";

/**
 * Takes in an object and returns error messages if error is an ApolloError. If
 * getting a user facing error, consider using `getUserFacingErrorMessage` or
 * `getUserFacingErroMessages` instead.
 */
export function getErrorMessagesWithCode(error: any, code: string): string[] {
  if (!(error instanceof ApolloError)) {
    return [];
  }
  const fromGraphQLErrors = getErrorMessagesFromErrors(
    error.graphQLErrors ?? [],
    code,
  );
  if (fromGraphQLErrors.length > 0) {
    return fromGraphQLErrors;
  }
  return getErrorMessagesFromErrors(errorsFromNetworkError(error), code);
}

/**
 * The coded errors Apollo Client filed under `networkError` rather than
 * `graphQLErrors`.
 *
 * Apollo Server answers a variable-coercion failure with HTTP 400 rather than
 * 200-with-errors (`status400ForVariableCoercionErrors`), and Apollo Client treats
 * any non-2xx as a `ServerError` -- so `graphQLErrors` is empty and the response
 * body, codes and all, is parked on `networkError.result`. Callers asking "was this
 * the user's fault?" would otherwise see nothing and fall through to a 500.
 *
 * Only reached when `graphQLErrors` is empty, so a normal 200-with-errors response
 * is unaffected.
 */
function errorsFromNetworkError(
  error: ApolloError,
): readonly GraphQLFormattedError[] {
  const result: unknown = (
    error.networkError as { result?: unknown } | null | undefined
  )?.result;
  if (typeof result !== "object" || result == null) {
    return [];
  }
  const errors: unknown = (result as { errors?: unknown }).errors;
  return Array.isArray(errors) ? (errors as GraphQLFormattedError[]) : [];
}

export function getErrorMessagesFromErrors(
  errors: readonly GraphQLFormattedError[],
  code: string,
): string[] {
  return errors.reduce<string[]>((errorMessages, err) => {
    if (err.extensions?.code === code) {
      errorMessages.push(err.message);
    }
    return errorMessages;
  }, []);
}

/**
 * Takes in an object an returns user facing error messages if error is an
 * ApolloError. Use this only if you have a multiple mutations/queries and
 * expect more than one user facing error. Otherwise, use
 * `getUserFacingErrorMessage`
 */
export function getUserFacingErrorMessages(error: any): string[] {
  return getErrorMessagesWithCode(error, GraphQLErrorCode.USER_FACING_ERROR);
}

/**
 * Takes in an object an returns user facing error messages if error is an
 * ApolloError.
 */
export function getUserFacingErrorMessage(error: any): string | undefined {
  return getUserFacingErrorMessages(error)[0];
}
